import React, { useState, useEffect } from 'react'
import '../Css/CareersApplicatinDetails.css'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { API_URL } from '../utils/Function';

export default function CareersApplicatioms({ career, onClose }) {
  const [formData, setFormData] = useState({
    career_id: career?.id || '',
    full_name: '',
    email: '',
    phone: '',
    current_location: '',
    experience_years: 0,
    current_company: '',
    current_designation: '',
    expected_salary: '',
    notice_period: '',
    cover_letter: '',
    portfolio_url: '',
    linkedin_url: '',
    resume: null
  });

  const [loading, setLoading] = useState(false);
  const [resumePreview, setResumePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (career) {
      setFormData(prev => ({ ...prev, career_id: career.id }));
    }
  }, [career]);

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'full_name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Full name must be at least 2 characters';
        return '';
        
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
        
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (!/^[\+]?[1-9][\d]{0,15}$/.test(value.replace(/[\s\-\(\)]/g, ''))) return 'Please enter a valid phone number';
        return '';
        
      case 'experience_years':
        if (value < 0) return 'Experience cannot be negative';
        return '';
        
      case 'portfolio_url':
      case 'linkedin_url':
        if (value && !/^https?:\/\/.+\..+/.test(value)) return 'Please enter a valid URL';
        return '';
        
      default:
        return '';
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.full_name.trim()) errors.full_name = 'Full name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.resume) errors.resume = 'Resume is required';
    
    // Format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    if (formData.portfolio_url && !/^https?:\/\/.+\..+/.test(formData.portfolio_url)) {
      errors.portfolio_url = 'Please enter a valid URL';
    }
    
    if (formData.linkedin_url && !/^https?:\/\/.+\..+/.test(formData.linkedin_url)) {
      errors.linkedin_url = 'Please enter a valid URL';
    }
    
    if (formData.experience_years < 0) {
      errors.experience_years = 'Experience cannot be negative';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));
    
    let newValue = value;
    
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      
      // Validate file
      if (file) {
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        const maxSize = 5 * 1024 * 1024; // 5MB
        
        if (!allowedTypes.includes(file.type)) {
          setFormErrors(prev => ({ ...prev, resume: 'Please upload a PDF or Word document' }));
        } else if (file.size > maxSize) {
          setFormErrors(prev => ({ ...prev, resume: 'File size must be less than 5MB' }));
        } else {
          setFormErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.resume;
            return newErrors;
          });
        }
        
        const reader = new FileReader();
        reader.onload = (e) => {
          setResumePreview({
            name: file.name,
            size: (file.size / 1024 / 1024).toFixed(2),
            type: file.type
          });
        };
        reader.readAsDataURL(file);
      } else {
        setResumePreview(null);
        setFormErrors(prev => ({ ...prev, resume: 'Resume is required' }));
      }
    } else {
      if (type === 'number') {
        newValue = value === '' ? '' : parseFloat(value);
      }
      
      setFormData(prev => ({ ...prev, [name]: newValue }));
      
      // Real-time validation - clear error when user starts typing
      const error = validateField(name, newValue);
      setFormErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    setFormErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const submitFormData = new FormData();
      
      // Append all form data
      Object.keys(formData).forEach(key => {
        if (key === 'resume' && formData[key]) {
          submitFormData.append(key, formData[key]);
        } else if (formData[key] !== null && formData[key] !== '') {
          submitFormData.append(key, formData[key]);
        }
      });

      const response = await fetch(`${API_URL}/career_application.php/post`, {
        method: 'POST',
        body: submitFormData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Application submitted successfully!');
        // Close modal after short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        toast.error(data.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('An error occurred while submitting the application');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Helper function to get input class - FIXED
  const getInputClass = (fieldName) => {
    if (!touched[fieldName]) {
      return 'form-input';
    }
    
    if (formErrors[fieldName]) {
      return 'form-input is-invalid';
    } else if (formData[fieldName] && formData[fieldName].toString().trim() !== '') {
      return 'form-input is-valid';
    }
    
    return 'form-input';
  };

  if (!career) return null;

  return (
    <>
      <ToastContainer 
        position="top-center" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <div className="career-application-modal-overlay" onClick={handleOverlayClick}>
        <div className="career-application-modal">
          <div className="application-modal-header">
            <div className="application-header-content">
              <h2>Apply for {career.title}</h2>
              <p className="application-subtitle">{career.department} • {career.location}</p>
            </div>
            <button className="application-close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="application-modal-body">
            <form onSubmit={handleSubmit} className="application-form" noValidate>
              {/* Personal Information Section */}
              <div className="form-section">
                <h3 className="section-title">Personal Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('full_name')}
                      placeholder="Enter your full name"
                      required
                    />
                    {touched.full_name && formErrors.full_name && (
                      <div className="invalid-feedback">{formErrors.full_name}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('email')}
                      placeholder="Enter your email"
                      required
                    />
                    {touched.email && formErrors.email && (
                      <div className="invalid-feedback">{formErrors.email}</div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Phone <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('phone')}
                      placeholder="Enter your phone number"
                      required
                    />
                    {touched.phone && formErrors.phone && (
                      <div className="invalid-feedback">{formErrors.phone}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Current Location</label>
                    <input
                      type="text"
                      name="current_location"
                      value={formData.current_location}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('current_location')}
                      placeholder="Enter your current location"
                    />
                    {touched.current_location && formErrors.current_location && (
                      <div className="invalid-feedback">{formErrors.current_location}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Professional Information Section */}
              <div className="form-section">
                <h3 className="section-title">Professional Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Experience (Years)</label>
                    <input
                      type="number"
                      name="experience_years"
                      value={formData.experience_years}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('experience_years')}
                      placeholder="Years of experience"
                      min="0"
                      step="0.5"
                    />
                    {touched.experience_years && formErrors.experience_years && (
                      <div className="invalid-feedback">{formErrors.experience_years}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Current Company</label>
                    <input
                      type="text"
                      name="current_company"
                      value={formData.current_company}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('current_company')}
                      placeholder="Your current company"
                    />
                    {touched.current_company && formErrors.current_company && (
                      <div className="invalid-feedback">{formErrors.current_company}</div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Current Designation</label>
                    <input
                      type="text"
                      name="current_designation"
                      value={formData.current_designation}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('current_designation')}
                      placeholder="Your current designation"
                    />
                    {touched.current_designation && formErrors.current_designation && (
                      <div className="invalid-feedback">{formErrors.current_designation}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Expected Salary</label>
                    <input
                      type="text"
                      name="expected_salary"
                      value={formData.expected_salary}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('expected_salary')}
                      placeholder="Expected salary (e.g., ₹10 LPA)"
                    />
                    {touched.expected_salary && formErrors.expected_salary && (
                      <div className="invalid-feedback">{formErrors.expected_salary}</div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Notice Period</label>
                    <select
                      name="notice_period"
                      value={formData.notice_period}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('notice_period')}
                    >
                      <option value="">Select notice period</option>
                      <option value="Immediate">Immediate</option>
                      <option value="15 days">15 days</option>
                      <option value="30 days">30 days</option>
                      <option value="60 days">60 days</option>
                      <option value="90 days">90 days</option>
                      <option value="More than 90 days">More than 90 days</option>
                    </select>
                    {touched.notice_period && formErrors.notice_period && (
                      <div className="invalid-feedback">{formErrors.notice_period}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Resume & Links Section */}
              <div className="form-section">
                <h3 className="section-title">Resume & Links</h3>
                
                <div className="form-group">
                  <label className="form-label">
                    Resume/CV <span className="required">*</span>
                  </label>
                  <div className={`file-upload-area ${touched.resume && formErrors.resume ? 'file-upload-error' : formData.resume ? 'file-upload-valid' : ''}`}>
                    <input
                      type="file"
                      name="resume"
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className="file-input"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      required
                    />
                    <div className="file-upload-content">
                      <div className="file-upload-icon">📄</div>
                      <div className="file-upload-text">
                        <p>Click to upload resume</p>
                        <span>PDF, DOC, DOCX (Max 5MB)</span>
                      </div>
                    </div>
                  </div>
                  {touched.resume && formErrors.resume && (
                    <div className="invalid-feedback" style={{display: 'block'}}>{formErrors.resume}</div>
                  )}
                  
                  {resumePreview && (
                    <div className="file-preview">
                      <span className="file-name">{resumePreview.name}</span>
                      <span className="file-size">({resumePreview.size} MB)</span>
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Portfolio URL</label>
                    <input
                      type="url"
                      name="portfolio_url"
                      value={formData.portfolio_url}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('portfolio_url')}
                      placeholder="https://yourportfolio.com"
                    />
                    {touched.portfolio_url && formErrors.portfolio_url && (
                      <div className="invalid-feedback">{formErrors.portfolio_url}</div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">LinkedIn URL</label>
                    <input
                      type="url"
                      name="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={getInputClass('linkedin_url')}
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                    {touched.linkedin_url && formErrors.linkedin_url && (
                      <div className="invalid-feedback">{formErrors.linkedin_url}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Cover Letter Section */}
              <div className="form-section">
                <h3 className="section-title">Cover Letter</h3>
                <div className="form-group">
                  <label className="form-label">Why are you interested in this position?</label>
                  <textarea
                    name="cover_letter"
                    value={formData.cover_letter}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={getInputClass('cover_letter')}
                    placeholder="Tell us why you're the perfect candidate for this role..."
                    rows="4"
                  />
                  {touched.cover_letter && formErrors.cover_letter && (
                    <div className="invalid-feedback">{formErrors.cover_letter}</div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}